import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const C = {
  red: "#E01A2E", redSoft: "#FCD5D8", navy: "#0B1E3B",
  body: "#F7F5F0", slate: "#5B6B7E", amber: "#F5A623",
  green: "#1E9E63", ink: "#1A1E24",
};

const font = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const serif = { fontFamily: "'Fraunces', 'Iowan Old Style', 'Palatino Linotype', Georgia, serif" };

// Single breakpoint for the whole landing page. Inline styles can't carry media queries,
// so layout values that differ on a phone are picked here instead.
function useIsMobile(bp = 767) {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const on = (e) => setM(e.matches);
    setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [bp]);
  return m;
}

// Inline SVG mark, scaled by `size`. Matches the ResQ1 shield: rounded top corners,
// pointed bottom, diagonal white slash carving through it.
const ResQShield = ({ size = 32, color = C.red, style = {} }) => (
  <svg
    width={size}
    height={size * 1.18}
    viewBox="0 0 60 71"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    aria-label="ResQ1"
  >
    <defs>
      <clipPath id={`rs-clip-${size}`}>
        <path d="M4 6 Q4 1 9 1 L51 1 Q56 1 56 6 L56 38 Q56 55 30 70 Q4 55 4 38 Z" />
      </clipPath>
    </defs>
    <g clipPath={`url(#rs-clip-${size})`}>
      <rect width="60" height="71" fill={color} />
      <path d="M-4 44 L30 20 L37 28 L3 56 Z" fill="#F7F5F0" />
    </g>
  </svg>
);

// Wordmark: RESQ1 in tight editorial caps
const ResQWordmark = ({ size = 22, color = C.ink }) => (
  <span style={{ fontWeight: 900, letterSpacing: "-0.04em", fontSize: size, color, lineHeight: 1 }}>
    RESQ<span style={{ color: C.red }}>1</span>
  </span>
);

// Tactical grid background (5% opacity)
const GridBg = ({ opacity = 0.05, dark = false }) => (
  <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity, pointerEvents: "none" }} aria-hidden>
    <defs>
      <pattern id={`g-${dark ? "d" : "l"}`} width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M 48 0 L 0 0 0 48" fill="none" stroke={dark ? "#fff" : C.slate} strokeWidth="0.5" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#g-${dark ? "d" : "l"})`} />
  </svg>
);

export default function MarketingPage() {
  return (
    <div style={{ ...font, background: C.body, color: C.ink, minHeight: "100vh" }} data-testid="marketing-page">
      <TopBar />
      <Hero />
      <Split />
      <HowItWorks />
      <PartnerValue />
      <Stats />
      <Footer />
    </div>
  );
}

/* ---------------- TOP BAR ---------------- */
function TopBar() {
  const mob = useIsMobile();
  return (
    <header
      style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(247,245,240,0.85)", backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${C.ink}0F`,
      }}
      data-testid="marketing-topbar"
    >
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: mob ? "12px 18px" : "14px 32px", display: "flex", alignItems: "center", gap: mob ? 12 : 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: mob ? 9 : 12 }} data-testid="marketing-brandmark">
          <ResQShield size={mob ? 22 : 26} />
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <ResQWordmark size={18} />
            <span style={{ ...mono, fontSize: 8.5, letterSpacing: "0.22em", color: C.slate, textTransform: "uppercase" }}>
              Emergency Protection
            </span>
          </div>
        </div>
        <nav style={{ display: mob ? "none" : "flex", gap: 26, marginLeft: 40, fontSize: 13, color: C.ink }}>
          <a href="#consumer" data-testid="nav-consumer" style={navLink}>ResQ1 Consumer</a>
          <a href="#commercial" data-testid="nav-commercial" style={navLink}>ResQconnect Commercial</a>
          <a href="#how" data-testid="nav-how" style={navLink}>How it works</a>
          <a href="#partners" data-testid="nav-partners" style={navLink}>Partner API</a>
        </nav>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <Link to="/admin" data-testid="nav-admin" style={{ ...mono, fontSize: 11, color: C.slate, textDecoration: "none", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Admin →
          </Link>
        </div>
      </div>
    </header>
  );
}
const navLink = { color: C.ink, textDecoration: "none", fontWeight: 500, letterSpacing: "-0.005em" };

/* ---------------- HERO ---------------- */
function Hero() {
  const mob = useIsMobile();
  return (
    <section style={{ position: "relative", overflow: "hidden" }} data-testid="marketing-hero">
      <GridBg opacity={0.06} />
      {/* Editorial bleed shield — huge, low opacity, anchored to top-left */}
      <div style={{ position: "absolute", top: mob ? -30 : -60, left: mob ? -50 : -80, opacity: 0.04, pointerEvents: "none", transform: "rotate(-8deg)" }} aria-hidden>
        <ResQShield size={mob ? 280 : 520} />
      </div>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: mob ? "44px 18px 56px" : "80px 32px 100px", display: "grid", gridTemplateColumns: mob ? "1fr" : "1.15fr 1fr", gap: mob ? 40 : 64, alignItems: "center", position: "relative" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "#fff", border: `1px solid ${C.ink}14`, borderRadius: 20, marginBottom: mob ? 20 : 28 }}>
            <span style={{ width: 6, height: 6, background: C.green, borderRadius: "50%", boxShadow: `0 0 0 4px ${C.green}22` }} />
            <span style={{ ...mono, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate }}>
              Platform Spec v1.8 · Live
            </span>
          </div>
          <h1 style={{
            ...serif,
            fontSize: "clamp(34px, 8.6vw, 84px)",
            lineHeight: 1.05,
            letterSpacing: "-0.022em",
            fontWeight: 500,
            marginBottom: mob ? 20 : 28,
            color: C.ink,
          }}>
            When <em style={{ ...serif, fontStyle: "italic", fontWeight: 400, color: C.slate }}>seconds</em> count,<br />
            we register and dispatch your emergency in <span style={{ color: C.red, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>under 30 seconds</span>.
          </h1>
          <p style={{ fontSize: mob ? 15.5 : 19, lineHeight: 1.6, color: C.slate, maxWidth: 580, marginBottom: mob ? 28 : 40, letterSpacing: "-0.005em" }}>
            ResQ1 is South Africa&apos;s on-demand emergency protection service — 24/7 armed response, medical, and roadside assistance backed by <span style={{ color: C.ink, fontWeight: 600 }}>600+ tactical teams nationally</span>. ResQconnect is the dispatch backbone behind it, now open to commercial workforces and partners.
          </p>
          <div style={{ display: "flex", gap: mob ? 12 : 20, alignItems: mob ? "stretch" : "center", flexDirection: mob ? "column" : "row" }}>
            <a href="#consumer" data-testid="cta-primary" style={mob ? { ...ctaPrimary, justifyContent: "center", padding: "16px 22px", fontSize: 15 } : ctaPrimary}>See ResQ1 for households <span style={{ opacity: 0.7, marginLeft: 4 }}>→</span></a>
            <a href="#commercial" data-testid="cta-secondary" style={mob ? { ...ctaSecondary, justifyContent: "center", padding: "14px 22px", border: `1px solid ${C.ink}22`, borderRadius: 999 } : ctaSecondary}>ResQconnect Commercial <span style={{ opacity: 0.55, marginLeft: 4 }}>→</span></a>
          </div>
          <div style={{ marginTop: mob ? 32 : 44, display: "flex", gap: mob ? 22 : 40, flexWrap: "wrap" }}>
            <MiniStat n="< 30s" label="Register & dispatch" mono />
            <MiniStat n="99.7%" label="Response uptime" mono />
            <MiniStat n="12k+" label="Members protected" mono />
          </div>
        </div>

        <HeroTacticalPreview />
      </div>
    </section>
  );
}

/* ---------------- HERO TACTICAL PREVIEW ---------------- */
function HeroTacticalPreview() {
  const mob = useIsMobile();
  const [tick, setTick] = useState(113);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const fmt = s => `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  const eta = Math.max(0, 254 - tick);
  const etaFmt = `${String(Math.floor(eta / 60)).padStart(2, "0")}:${String(eta % 60).padStart(2, "0")}`;

  // SVG viewBox is 440 x 360. Panic point at (cx, cy). Teams arrayed around it.
  const cx = 220, cy = 175;
  const teams = [
    { code: "RT-04", x: 78,  y: 62,  color: C.amber,   label: "en route" },
    { code: "RT-07", x: 348, y: 88,  color: "#7a8ba0", label: "en route" },
    { code: "RT-12", x: 96,  y: 288, color: "#7a8ba0", label: "en route" },
    { code: "RT-19", x: 336, y: 268, color: C.green,   label: "on scene" },
  ];

  return (
    <div style={{ position: "relative" }} data-testid="hero-tactical-preview">
      {/* red glow behind */}
      <div style={{ position: "absolute", inset: -60, background: `radial-gradient(circle at 55% 45%, ${C.red}44, transparent 60%)`, filter: "blur(50px)", pointerEvents: "none" }} />
      {/* amber accent bottom-right */}
      <div style={{ position: "absolute", inset: -60, background: `radial-gradient(circle at 85% 90%, ${C.amber}33, transparent 55%)`, filter: "blur(50px)", pointerEvents: "none" }} />

      <div style={{
        position: "relative",
        background: "linear-gradient(160deg, #0e1e37 0%, #050c1a 100%)",
        borderRadius: 16, overflow: "hidden",
        boxShadow: "0 40px 100px rgba(11,30,59,0.55), 0 8px 30px rgba(0,0,0,0.28)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}>
        {/* Window chrome */}
        <div style={{ padding: "12px 16px", background: "rgba(0,0,0,0.28)", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <span style={dot("#ff5f56")} /><span style={dot("#febc2e")} /><span style={dot("#28c840")} />
          </div>
          <span style={{ ...mono, fontSize: 10, letterSpacing: "0.14em", color: "rgba(255,255,255,0.45)", textTransform: "uppercase", marginLeft: 8 }}>
            ops.resqconnect · dispatch
          </span>
          <span style={{ marginLeft: "auto", ...mono, fontSize: 10, color: C.green, letterSpacing: "0.14em", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 6, height: 6, background: C.green, borderRadius: "50%", boxShadow: `0 0 0 3px ${C.green}22` }} />
            LIVE · 3 ACTIVE
          </span>
        </div>

        {/* Tactical map */}
        <div style={{ position: "relative", height: mob ? 260 : 400 }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 48%, #14335c 0%, #050c19 72%)" }} />

          <svg width="100%" height="100%" viewBox="0 0 440 360" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, display: "block" }}>
            <defs>
              <pattern id="tac-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#ffffff" strokeWidth="0.3" opacity="0.08" />
              </pattern>
              <radialGradient id="tac-hot">
                <stop offset="0%" stopColor={C.red} stopOpacity="0.38" />
                <stop offset="100%" stopColor={C.red} stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect width="440" height="360" fill="url(#tac-grid)" />
            <circle cx={cx} cy={cy} r="150" fill="url(#tac-hot)" />

            {/* contour rings */}
            {[160, 118, 78, 46].map((r, i) => (
              <circle key={r} cx={cx} cy={cy} r={r}
                fill="none"
                stroke={C.red}
                strokeOpacity={0.07 + i * 0.06}
                strokeWidth="1"
                strokeDasharray={i < 2 ? "2 6" : undefined} />
            ))}

            {/* pulse ripples */}
            {[0, 0.8, 1.6].map((delay, i) => (
              <circle key={i} cx={cx} cy={cy} r="16" fill="none" stroke={C.red} strokeWidth="1.5">
                <animate attributeName="r" from="16" to="152" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.75" to="0" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
              </circle>
            ))}

            {/* response vectors — dashed lines from each team to center */}
            {teams.map(t => (
              <line key={`v-${t.code}`}
                x1={t.x} y1={t.y} x2={cx} y2={cy}
                stroke={t.color} strokeOpacity="0.42" strokeWidth="1"
                strokeDasharray="3 5"
                style={{ animation: "hero-flow 1.4s linear infinite" }} />
            ))}

            {/* team markers */}
            {teams.map(t => (
              <g key={t.code} transform={`translate(${t.x}, ${t.y})`}>
                <circle r="16" fill={t.color} opacity="0.12" />
                <circle r="9" fill={t.color} opacity="0.9" />
                <circle r="4" fill="#fff" />
                <g transform="translate(14, -8)">
                  <rect x="0" y="0" width="56" height="17" rx="3"
                    fill="rgba(0,0,0,0.65)" stroke={t.color} strokeOpacity="0.55" strokeWidth="0.5" />
                  <text x="6" y="12" fontFamily="'JetBrains Mono', ui-monospace, monospace"
                    fontSize="9" fill="#fff" letterSpacing="0.05em" fontWeight="600">{t.code}</text>
                </g>
              </g>
            ))}

            {/* panic marker at center */}
            <g transform={`translate(${cx}, ${cy})`}>
              <circle r="12" fill={C.red} opacity="0.2" />
              <circle r="6" fill={C.red} />
              <circle r="2.5" fill="#fff" />
              <line x1="-16" y1="0" x2="-10" y2="0" stroke={C.red} strokeWidth="1.4" />
              <line x1="10"  y1="0" x2="16"  y2="0" stroke={C.red} strokeWidth="1.4" />
              <line x1="0" y1="-16" x2="0" y2="-10" stroke={C.red} strokeWidth="1.4" />
              <line x1="0" y1="10"  x2="0" y2="16"  stroke={C.red} strokeWidth="1.4" />
            </g>

            {/* corner reticles */}
            {[[16, 16], [424, 16], [16, 344], [424, 344]].map(([x, y], i) => (
              <g key={i} transform={`translate(${x}, ${y})`} stroke="rgba(255,255,255,0.2)" strokeWidth="1">
                <line x1="-7" y1="0" x2="7" y2="0" />
                <line x1="0" y1="-7" x2="0" y2="7" />
              </g>
            ))}
          </svg>

          {/* Corner HUD text */}
          <div style={{ position: "absolute", top: 14, left: 16, ...mono, fontSize: 9.5, color: "rgba(255,255,255,0.65)", letterSpacing: "0.14em", lineHeight: 1.5 }}>
            <div style={{ opacity: 0.5, fontSize: 8.5 }}>PANIC AT</div>
            -25.833, 28.165
            <div style={{ opacity: 0.5, marginTop: 2 }}>KIRKWOOD · EC</div>
          </div>
          <div style={{ position: "absolute", top: 14, right: 16, textAlign: "right" }}>
            <div style={{ ...mono, fontSize: 8.5, color: "rgba(255,255,255,0.4)", letterSpacing: "0.14em", marginBottom: 2 }}>NEAREST ETA</div>
            <div style={{ ...mono, fontSize: 22, color: C.amber, letterSpacing: "0.02em", fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{etaFmt}</div>
            <div style={{ ...mono, fontSize: 8, color: "rgba(255,255,255,0.4)", letterSpacing: "0.14em", marginTop: 4 }}>RT-04 CONVERGING</div>
          </div>
          <div style={{ position: "absolute", bottom: 14, left: 16, display: "flex", gap: 14, ...mono, fontSize: 9, letterSpacing: "0.12em" }}>
            <span style={{ color: C.green }}>● 1 ON SCENE</span>
            <span style={{ color: C.amber }}>● 3 EN ROUTE</span>
          </div>
          <div style={{ position: "absolute", bottom: 14, right: 16, ...mono, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.14em" }}>
            RV.4.2.1
          </div>
        </div>

        {/* Bottom incident detail */}
        <div style={{ padding: 14, borderTop: "1px solid rgba(255,255,255,0.07)", background: "rgba(0,0,0,0.28)" }}>
          <div style={{ background: "rgba(255,255,255,0.05)", padding: 12, borderRadius: 8, borderLeft: `3px solid ${C.red}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: "-0.01em" }}>MARYANNE B.</div>
              <div style={{ ...mono, fontSize: 12, color: C.red, fontVariantNumeric: "tabular-nums" }}>{fmt(tick)}</div>
            </div>
            <div style={{ ...mono, fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
              +27 72 555 0143 · Household member · 4 dependents
            </div>
            <div style={{ fontSize: 10, color: C.red, marginTop: 6, letterSpacing: "0.06em", fontWeight: 600 }}>
              ▶ ROADSIDE EMERGENCY · DISPATCHED · RT-19 ON SCENE
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hero-flow {
          to { stroke-dashoffset: -16; }
        }
      `}</style>
    </div>
  );
}
const dot = c => ({ width: 10, height: 10, background: c, borderRadius: "50%" });
const ctaPrimary = {
  padding: "14px 22px", background: C.ink, color: "#fff", borderRadius: 999,
  fontWeight: 500, fontSize: 14, textDecoration: "none", letterSpacing: "-0.005em",
  display: "inline-flex", alignItems: "center",
};
const ctaSecondary = {
  padding: "14px 22px", background: "transparent", color: C.ink,
  borderRadius: 999, fontWeight: 500, fontSize: 14, textDecoration: "none", letterSpacing: "-0.005em",
  display: "inline-flex", alignItems: "center", borderBottom: `1px solid ${C.ink}22`,
};
const MiniStat = ({ n, label, mono: isMono }) => (
  <div>
    <div style={{ ...(isMono ? mono : {}), fontSize: 22, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{n}</div>
    <div style={{ fontSize: 11, color: C.slate, marginTop: 2, letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</div>
  </div>
);

/* ---------------- DUAL PRODUCT SPLIT ---------------- */
function Split() {
  const mob = useIsMobile();
  return (
    <section style={{ background: "#fff", borderTop: `1px solid ${C.ink}0F`, borderBottom: `1px solid ${C.ink}0F` }} data-testid="marketing-split">
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: mob ? "56px 18px" : "80px 32px" }}>
        <SectionKicker>ONE PLATFORM · TWO PRODUCTS</SectionKicker>
        <h2 style={h2}>Built for <em style={{ ...serif, fontStyle: "italic", fontWeight: 400 }}>households</em>.<br /><span style={{ color: C.slate }}>Ready for every workforce.</span></h2>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: mob ? 18 : 24, marginTop: mob ? 34 : 56 }}>
          {/* Consumer */}
          <div id="consumer" style={{ ...productCard, padding: mob ? 24 : 40, position: "relative", overflow: "hidden" }} data-testid="product-consumer">
            {/* Editorial shield accent — top-right of card */}
            <div style={{ position: "absolute", top: -30, right: -30, opacity: 0.05, pointerEvents: "none" }} aria-hidden>
              <ResQShield size={220} />
            </div>
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <ResQShield size={30} />
                <ResQWordmark size={22} />
                <span style={badge(C.green)}>CONSUMER · LIVE</span>
              </div>
              <h3 style={h3}>ResQ1 for households</h3>
              <p style={pMuted}>
                A dead-simple PWA installed on any phone in 30 seconds. Hold to panic, and the entire family knows — with real-time location, battery status, and dispatch straight to your armed response.
              </p>
              <ul style={ul}>
                <FeatureLi label="Long-press panic with cancel-window" />
                <FeatureLi label="Household roster · request access to any member's location" />
                <FeatureLi label="Test-my-safety-net drill mode" />
                <FeatureLi label="Fallback tel-link if the network fails" />
                <FeatureLi label="Guided install for iPhone and Android — under a minute" />
              </ul>
              <div style={{ marginTop: 28, padding: 16, background: C.body, borderRadius: 10, border: `1px dashed ${C.ink}14`, ...mono, fontSize: 11, color: C.slate }}>
                For: parents, elderly, medical patients, road-users.
              </div>
            </div>
          </div>

          {/* Commercial */}
          <div id="commercial" style={{ ...productCard, padding: mob ? 24 : 40, background: C.navy, color: "#fff", position: "relative", overflow: "hidden" }} data-testid="product-commercial">
            <GridBg opacity={0.06} dark />
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", color: "#fff" }}>
                  ResQ<span style={{ color: C.red }}>connect</span>
                </span>
                <span style={{ ...badge(C.amber), background: C.amber, color: C.navy }}>COMMERCIAL · WORKFORCE</span>
              </div>
              <h3 style={{ ...h3, color: "#fff" }}>Protect your people at work.</h3>
              <p style={{ ...pMuted, color: "rgba(255,255,255,0.7)" }}>
                Every driver, field technician, delivery agent, and lone worker gets the same one-tap panic that a household member gets — with the operations team watching the map behind them. Because your duty of care doesn't stop at the depot gate.
              </p>
              <ul style={{ ...ul, color: "rgba(255,255,255,0.85)" }}>
                <FeatureLi dark label="One-tap panic on every driver's phone — no separate device" />
                <FeatureLi dark label="Live GPS + battery visibility of every worker on shift" />
                <FeatureLi dark label="Duress check-ins and journey-monitoring for long-haul routes" />
                <FeatureLi dark label="Manager dashboard: who's on shift, who's OK, who needs help" />
                <FeatureLi dark label="Same < 30 second dispatch — for your people, not just customers" />
              </ul>
              <div style={{ marginTop: 28, padding: 16, background: "rgba(255,255,255,0.06)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", ...mono, fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
                For: logistics fleets · security guarding · field service teams · lone workers · delivery networks.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
const productCard = {
  background: "#fff", border: `1px solid ${C.ink}0E`, borderRadius: 14, padding: 40,
};
const h2 = { ...serif, fontSize: "clamp(29px, 6.4vw, 62px)", fontWeight: 500, letterSpacing: "-0.022em", lineHeight: 1.08, color: C.ink, margin: 0 };
const h3 = { ...serif, fontSize: "clamp(23px, 5.4vw, 30px)", fontWeight: 500, letterSpacing: "-0.018em", marginBottom: 14, color: C.ink, lineHeight: 1.15 };
const pMuted = { fontSize: 15, color: C.slate, lineHeight: 1.55, marginBottom: 24, letterSpacing: "-0.003em" };
const ul = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10, color: C.ink };
const badge = c => ({
  ...mono, fontSize: 10, background: `${c}18`, color: c, padding: "4px 8px", borderRadius: 4,
  letterSpacing: "0.14em", fontWeight: 600, textTransform: "uppercase",
});
function FeatureLi({ label, dark }) {
  return (
    <li style={{ display: "flex", gap: 10, fontSize: 14, alignItems: "flex-start" }}>
      <span style={{
        width: 16, height: 16, borderRadius: 3, background: dark ? "rgba(224,26,46,0.2)" : `${C.red}18`,
        color: C.red, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10,
        flexShrink: 0, marginTop: 2,
      }}>✓</span>
      <span>{label}</span>
    </li>
  );
}
const SectionKicker = ({ children }) => (
  <div style={{ ...mono, fontSize: 11, color: C.slate, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
    <span style={{ width: 24, height: 1, background: C.red }} /> {children}
  </div>
);

/* ---------------- HOW IT WORKS ---------------- */
function HowItWorks() {
  const steps = [
    { n: "01", t: "The signal", d: "A tap. A hold. A call for help from your family app, driver device, or dispatch inbox — heard the instant it fires, day or night.", time: "T + 0s" },
    { n: "02", t: "The picture", d: "Who you are. Where you are. What you need. Locked in and pushed to responders before the first ring finishes.", time: "T + 2s" },
    { n: "03", t: "Boots move", d: "The nearest armed team is dispatched in under 30 seconds. Every action logged. Every second auditable.", time: "T + 4s" },
    { n: "04", t: "On scene", d: "Armed officers, medics, or roadside crew converge on your location. You are not alone — you never were.", time: "T + on scene" },
  ];
  const mob = useIsMobile();
  return (
    <section id="how" style={{ background: C.body, position: "relative" }} data-testid="marketing-how">
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: mob ? "60px 18px" : "96px 32px" }}>
        <SectionKicker>THE PIPELINE</SectionKicker>
        <h2 style={h2}>From panic to armed response, <em style={{ ...serif, fontStyle: "italic", fontWeight: 400, color: C.red }}>in four steps</em>.</h2>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "repeat(4, 1fr)", gap: mob ? 14 : 20, marginTop: mob ? 34 : 56 }}>
          {steps.map((s, i) => (
            <div key={s.n} data-testid={`step-${s.n}`} style={{
              padding: 24, background: "#fff", border: `1px solid ${C.ink}10`, borderRadius: 12,
              position: "relative",
            }}>
              <div style={{ ...mono, fontSize: 32, fontWeight: 700, color: C.red, letterSpacing: "-0.03em", marginBottom: 12 }}>{s.n}</div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.015em", marginBottom: 8, color: C.ink }}>{s.t}</div>
              <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.55, marginBottom: 16 }}>{s.d}</div>
              <div style={{ ...mono, fontSize: 10, color: C.navy, letterSpacing: "0.1em", padding: "4px 8px", background: `${C.navy}0C`, display: "inline-block", borderRadius: 4 }}>
                {s.time}
              </div>
              {i < 3 && !mob && (
                <div style={{ position: "absolute", right: -14, top: "50%", color: C.slate, fontSize: 18, opacity: 0.5 }}>→</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PARTNER VALUE PROP ---------------- */
function PartnerValue() {
  const mob = useIsMobile();
  return (
    <section id="partners" style={{ background: "#fff", borderTop: `1px solid ${C.ink}0F` }} data-testid="marketing-partners">
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: mob ? "60px 18px" : "96px 32px" }}>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1.4fr", gap: mob ? 40 : 80, alignItems: "start" }}>
          <div>
            <SectionKicker>PARTNER API</SectionKicker>
            <h2 style={{ ...h2, fontSize: "clamp(27px, 5.6vw, 50px)" }}>The dispatch layer, <em style={{ ...serif, fontStyle: "italic", fontWeight: 400, color: C.slate }}>quietly</em> under your brand.</h2>
            <p style={{ ...pMuted, marginTop: 24, fontSize: mob ? 15 : 16 }}>
              Insurers, roadside networks, and security operators plug into ResQconnect's dispatch backbone via API, webhook, or inbound email. Your customers see your brand — we handle the routing to armed response in the background.
            </p>
            <ul style={{ ...ul, marginTop: 24, marginBottom: 32 }}>
              <FeatureLi label="REST API + inbound-email intake" />
              <FeatureLi label="3-tier geocoding (Google · Photon · Nominatim)" />
              <FeatureLi label="Auto-dispatch to armed response within 4s" />
              <FeatureLi label="White-label domain · your partner's brand" />
              <FeatureLi label="99.7% SLA · signed webhook receipts" />
            </ul>
            <div style={{ display: "flex", gap: mob ? 20 : 24, flexWrap: "wrap" }}>
              <MiniStat n="4s" label="Dispatch SLA" mono />
              <MiniStat n="99.7%" label="Uptime" mono />
              <MiniStat n="24/7" label="Control room" mono />
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 10, color: C.slate, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 20 }}>
              LIVE INTEGRATION EXAMPLE
            </div>
            {/* API preview */}
            <div style={{ background: C.ink, borderRadius: 10, overflow: "hidden", boxShadow: "0 20px 40px rgba(26,30,36,0.2)" }}>
              <div style={{ padding: "10px 14px", background: "#000", ...mono, fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                POST /api/inbound/webhook
              </div>
              <pre style={{ ...mono, fontSize: mob ? 10.5 : 12, color: "#e6e6e6", padding: mob ? 14 : 18, margin: 0, lineHeight: 1.65, overflowX: "auto" }}>
{`{
  "partner_id": "acme_fleet",
  "member_ref": "SIM-88214",
  "name": "Maryanne B.",
  "cell": "+27725550143",
  "type": "roadside",
  "lat": -25.83386,
  "lng": 28.16592
}
`}
                <span style={{ color: C.green }}>{`→ 200 OK  incident_id: ${'"'}RSQ-2026-01812${'"'}`}</span>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- STATS / DIFFERENTIATORS ---------------- */
function Stats() {
  const mob = useIsMobile();
  return (
    <section style={{ background: C.navy, color: "#fff", position: "relative", overflow: "hidden" }} data-testid="marketing-stats">
      <GridBg opacity={0.06} dark />
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: mob ? "56px 18px" : "88px 32px", position: "relative" }}>
        <div style={{ ...mono, fontSize: 11, color: "rgba(255,255,255,0.55)", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 24, height: 1, background: C.red }} /> WHY RESQ1
        </div>
        <h2 style={{ ...serif, fontSize: "clamp(28px, 6.2vw, 54px)", fontWeight: 500, letterSpacing: "-0.022em", lineHeight: 1.08, color: "#fff", margin: 0, marginBottom: 8, maxWidth: 900 }}>
          Eight years of tactical dispatch.{" "}
          <em style={{ ...serif, fontStyle: "italic", fontWeight: 400, color: C.red }}>A flawless track record.</em>
        </h2>
        <p style={{ fontSize: mob ? 15.5 : 17, lineHeight: 1.6, color: "rgba(255,255,255,0.65)", maxWidth: 640, letterSpacing: "-0.005em", margin: mob ? "16px 0 36px" : "16px 0 56px" }}>
          Where most operators auto-dispatch alarm guards and hope for the best, ResQ1 routes every panic through a live command &amp; control room to a proper tactical team — with an SLA behind every dispatch.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: mob ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: mob ? 26 : 32 }}>
          {[
            { n: "8 yrs", l: "Flawless dispatch record" },
            { n: "100%", l: "SLA-backed response" },
            { n: "24/7", l: "Command & control room" },
            { n: "Tactical", l: "Teams — not alarm guards", isWord: true },
          ].map(s => (
            <div key={s.l}>
              <div
                style={{
                  ...mono,
                  fontSize: mob ? (s.isWord ? 26 : 34) : (s.isWord ? 34 : 44),
                  fontWeight: 700,
                  letterSpacing: s.isWord ? "-0.01em" : "-0.02em",
                  color: "#fff",
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1,
                }}
              >
                {s.n}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 10, letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.4 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FOOTER ---------------- */
function Footer() {
  const mob = useIsMobile();
  return (
    <footer style={{ background: C.ink, color: "rgba(255,255,255,0.6)", padding: mob ? "40px 18px 26px" : "56px 32px 32px", position: "relative", overflow: "hidden" }} data-testid="marketing-footer">
      {/* Editorial shield bleed in footer */}
      <div style={{ position: "absolute", bottom: -80, right: -40, opacity: 0.05, pointerEvents: "none" }} aria-hidden>
        <ResQShield size={mob ? 220 : 360} />
      </div>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: mob ? "1fr 1fr" : "1.4fr 1fr 1fr 1fr", gap: mob ? 26 : 40, position: "relative" }}>
        <div style={mob ? { gridColumn: "1 / -1" } : undefined}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <ResQShield size={28} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <ResQWordmark size={20} color="#fff" />
              <span style={{ ...mono, fontSize: 9, letterSpacing: "0.22em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>
                Emergency Protection
              </span>
            </div>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.55, maxWidth: 340 }}>
            South Africa's on-demand emergency protection service. 24/7 armed response, medical, and roadside assistance — backed by 600+ tactical teams nationally. ResQconnect is our dispatch platform, extending the same protection to commercial workforces and partner networks.
          </p>
        </div>
        <FooterCol title="Products">
          <a href="#consumer" style={fLink}>ResQ1 Consumer</a>
          <a href="#commercial" style={fLink}>ResQconnect Commercial</a>
          <a href="#how" style={fLink}>How it works</a>
        </FooterCol>
        <FooterCol title="For Partners">
          <a href="#partners" style={fLink}>Partner API</a>
          <a href="mailto:genevieve@resq1.co.za" style={fLink}>Book a briefing</a>
          <Link to="/admin" style={fLink}>Admin console</Link>
        </FooterCol>
        <FooterCol title="Contact">
          <a href="tel:0648379205" style={{ ...fLink, ...mono, fontSize: 13 }}>064 837 9205</a>
          <a href="mailto:info@resq1.co.za" style={fLink}>info@resq1.co.za</a>
          <a href="mailto:genevieve@resq1.co.za" style={{ ...fLink, fontSize: 12 }}>
            Genevieve · COO
          </a>
        </FooterCol>
      </div>
      <div style={{ maxWidth: 1280, margin: mob ? "32px auto 0" : "48px auto 0", paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center", ...mono, fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em", position: "relative", flexWrap: "wrap", gap: 12 }}>
        <span>© 2026 RESQ1 · RESCUE ONE (PTY) LTD</span>
        <div style={{ display: "flex", gap: 20 }}>
          <Link to="/terms" data-testid="footer-terms" style={{ color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>TERMS</Link>
          <Link to="/privacy" data-testid="footer-privacy" style={{ color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>PRIVACY</Link>
          <span style={{ color: "rgba(255,255,255,0.35)" }}>PLATFORM v1.8</span>
        </div>
      </div>
    </footer>
  );
}
function FooterCol({ title, children }) {
  return (
    <div>
      <div style={{ ...mono, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 14 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}
const fLink = { color: "rgba(255,255,255,0.65)", fontSize: 13, textDecoration: "none" };
